/**
 * Module dependencies.
 */

import { getGrapghqlEndpoint } from './resolve-admin';
import getConfig from 'next/config';

/**
 * Retry settings.
 *
 * O Directus tem um pressure limiter ligado por omissao
 * (`PRESSURE_LIMITER_MAX_EVENT_LOOP_DELAY: 500`) que faz load shedding quando o
 * event loop dele atrasa: responde HTTP 200 mas com um erro GraphQL de codigo
 * `SERVICE_UNAVAILABLE`. Sem retry, uma unica rejeicao rebenta a pagina -- e no
 * `next build` a primeira pagina rebentada aborta o build inteiro. Como o
 * `PRESSURE_LIMITER_RETRY_AFTER` esta a `false`, o Directus nao diz quanto
 * esperar, por isso usamos backoff exponencial com jitter.
 *
 * So se repete o que vale a pena repetir: rejeicoes por pressao e falhas de
 * rede. Um erro GraphQL normal continua a falhar a primeira, como antes.
 */

const MAX_ATTEMPTS = 4;
const RETRY_BASE_DELAY = 400;

/**
 * Service unavailable error code.
 */

const SERVICE_UNAVAILABLE = 'SERVICE_UNAVAILABLE';

/**
 * Is under pressure.
 */

function isUnderPressure(errors: any[]): boolean {
  return errors?.some(
    (error) => error?.extensions?.code === SERVICE_UNAVAILABLE,
  );
}

/**
 * Backoff delay for a given attempt, with jitter.
 */

function backoffDelay(attempt: number): number {
  return (
    RETRY_BASE_DELAY * 2 ** (attempt - 1) +
    Math.floor(Math.random() * RETRY_BASE_DELAY)
  );
}

/**
 * Sleep.
 */

function sleep(milliseconds: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, milliseconds));
}

/**
 * Hash string.
 */

export async function hashString(input: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(input);
  const hashBuffer = await crypto.subtle.digest('SHA-256', data);
  const hashArray = Array.from(new Uint8Array(hashBuffer));

  return hashArray.map((blob) => blob.toString(16).padStart(2, '0')).join('');
}

/**
 * Export `fetchGraphql`.
 */

export async function fetchGraphql(query: string) {
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    let payload: any;

    try {
      const response = await fetch(getGrapghqlEndpoint(), {
        body: JSON.stringify({ query }),
        headers: {
          Authorization: `Bearer ${getConfig().serverRuntimeConfig.directusToken}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
      });

      payload = await response.json();
    } catch (error) {
      if (attempt === MAX_ATTEMPTS) {
        // eslint-disable-next-line no-console
        console.error(error);

        throw error;
      }

      await sleep(backoffDelay(attempt));

      continue;
    }

    const { data, errors } = payload;

    if (errors) {
      if (isUnderPressure(errors) && attempt < MAX_ATTEMPTS) {
        // eslint-disable-next-line no-console
        console.warn(
          `Directus under pressure, retrying (attempt ${attempt}/${MAX_ATTEMPTS})`,
        );

        await sleep(backoffDelay(attempt));

        continue;
      }

      // eslint-disable-next-line no-console
      console.log('JSON errors: ', JSON.stringify(errors, null, 2));

      throw new Error(errors[0].message);
    }

    return data;
  }
}
